import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  Trash2,
  Check,
  Copy,
  Mail,
  Phone,
  MapPin,
  Package,
  ShoppingBag,
  Calendar,
  ArrowLeft,
  User,
  Info,
  ChevronRight,
  MessageCircle,
  Send,
  Loader,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardFooter,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

// Couleurs du drapeau du Niger
const COLORS = {
  orange: "#E87E04",
  white: "#FFFFFF",
  green: "#008751",
  darkGreen: "#006B3F",
  lightOrange: "#FFE0B2",
  lightGreen: "#CCFFCC",
  gray: "#718096",
  lightGray: "#E2E8F0",
  paleGreen: "#E6FFEA",
};

const BackendUrl = process.env.REACT_APP_Backend_Url;

function ModernCustomerDetails() {
  const [allUsers, setAllUsers] = useState(null);
  const [allProfiles, setAllProfiles] = useState(null);
  const [allAddress, setAllAddress] = useState(null);
  const [allCommandes, setAllCommandes] = useState(null);
  const [reduction, setReduction] = useState(0);
  const [dateExpired, setDateExpired] = useState(null);
  const [allCode, setAllCode] = useState(null);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("profile");
  const params = useParams();

  const admin = JSON.parse(localStorage.getItem("AdminEcomme"));

  if (admin) {
    axios.defaults.headers.common["Authorization"] = `Bearer ${admin.token}`;
  }

  const formatDate = (date) => {
    const options = { day: "numeric", month: "numeric", year: "numeric" };
    return new Date(date).toLocaleDateString("fr-FR", options);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "EUR",
    }).format(amount);
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [commandes, users, profiles, addresses] = await Promise.all([
          axios.get(`${BackendUrl}/api/ordersRoutes/`),
          axios.get(`${BackendUrl}/api/user/getUsers`),
          axios.get(`${BackendUrl}/api/profilesRoutes/all`),
          axios.get(`${BackendUrl}/api/shippingAddressRoutes/all`),
        ]);

        setAllCommandes(commandes.data.commandes);
        setAllUsers(users.data?.user);
        setAllProfiles(profiles.data.data);
        setAllAddress(addresses.data.data);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching data:", error);
        setLoading(false);
      }
    };

    fetchData();
  }, [params.id]);
  const handleChange = (e) => {
    setMessage(e.target.value);
  };
  const handleSendEmail = async (e) => {
    e.preventDefault();
    try {
      if (!currentUserDetails?.email) {
        alert("Erreur: Aucun email d'expéditeur disponible.");
        return;
      }

      if (!message || message.trim() === "") {
        alert("Erreur: Le message est vide.");
        return;
      }
      setIsLoading(true);

      const emailData = {
        senderEmail: currentUserDetails.email,
        subject: "Message de l'équipe Kassarmou Web",
        message: `
          <div style="font-family: Arial, sans-serif; font-size: 14px; color: #333;">
            ${message}
          </div>
        `,
        titel: `
          <div style="font-family: Arial, sans-serif; font-size: 18px; font-weight: bold; margin-top: 20px;">
            Vous avez reçu un message de notre équipe Kassarmou Web
          </div>
        `,
      };

      // Endpoint mail legacy désactivé côté backend: éviter un appel cassé.
      await Promise.resolve(emailData);
      alert("Envoi d'email désactivé temporairement pour des raisons de sécurité.");
      setMessage("");
    } catch (error) {
      alert("Erreur lors de l'envoi de l'email:");
      console.error("Erreur lors de l'envoi de l'email:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const currentUser = allAddress?.find((item) => item.clefUser === params.id);
  const currentProfile = allProfiles?.find(
    (item) => item.clefUser === params.id
  );
  const currentUserDetails = allUsers?.find((item) => item._id === params.id);
  const userCommandes =
    allCommandes?.filter((item) => item.clefUser === params.id) || [];
  // Calculer les statistiques des commandes
  const totalCommandes = userCommandes.length;
  const enCoursCommandes = userCommandes.filter(
    (item) => item.statusLivraison === "en cours"
  ).length;
  const livreesCommandes = userCommandes.filter(
    (item) => item.statusLivraison === "livré"
  ).length;

  const totalDepenses = userCommandes.reduce((total, commande) => {
    return total + (commande.prixTotal || 0);
  }, 0);

  const CopyButton = ({ code }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
      navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    };

    return (
      <Button
        variant="outline"
        size="sm"
        onClick={handleCopy}
        className="h-8 px-2 lg:px-3"
        style={{
          borderColor: COLORS.green,
          color: copied ? COLORS.green : COLORS.gray,
        }}
      >
        {copied ? (
          <>
            <Check className="h-3 w-3 mr-2" />
            <span className="text-xs">Copié!</span>
          </>
        ) : (
          <>
            <Copy className="h-3 w-3 mr-2" />
            <span className="text-xs">Copier</span>
          </>
        )}
      </Button>
    );
  };

  // Composant pour afficher un état de chargement
  if (loading) {
    return (
      <div className="container mx-auto h-screen flex items-center justify-center">
        <div className="text-center">
          <div
            className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 mx-auto mb-4"
            style={{ borderColor: COLORS.orange }}
          ></div>
          <p style={{ color: COLORS.green }}>
            Chargement des informations client...
          </p>
        </div>
      </div>
    );
  }

  // Si aucune donnée n'est trouvée
  if (!currentUserDetails && !currentUser) {
    return (
      <div className="container mx-auto p-6">
        <Card style={{ borderTop: `4px solid ${COLORS.orange}` }}>
          <CardContent className="pt-6 text-center">
            <Info
              size={48}
              className="mx-auto mb-4"
              style={{ color: COLORS.orange }}
            />
            <h2
              className="text-2xl font-bold mb-2"
              style={{ color: COLORS.green }}
            >
              Client non trouvé
            </h2>
            <p className="text-gray-500 mb-6">
              Les informations de ce client ne sont pas disponibles.
            </p>
            <Button style={{ backgroundColor: COLORS.green }}>
              <ArrowLeft className="mr-2 h-4 w-4" /> Retour à la liste
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div
      className="container mx-auto p-4 md:p-6"
      style={{ backgroundColor: COLORS.paleGreen }}
    >
      <div className="mb-6 flex items-center space-x-2">
        <Button
          variant="outline"
          className="flex items-center"
          style={{
            borderColor: COLORS.green,
            color: COLORS.green,
            backgroundColor: COLORS.white,
          }}
        >
          <ArrowLeft className="mr-2 h-4 w-4" /> Retour à la liste
        </Button>
        <h1 className="text-2xl font-bold" style={{ color: COLORS.green }}>
          Détails du client
        </h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Colonne de gauche - Profil */}
        <div className="lg:col-span-1 space-y-6">
          <Card
            style={{
              backgroundColor: COLORS.white,
              borderTop: `4px solid ${COLORS.orange}`,
              overflow: "hidden",
            }}
          >
            <div
              className="h-24"
              style={{ backgroundColor: COLORS.lightGreen }}
            ></div>
            <CardContent className="-mt-12 relative pb-6">
              <div className="flex flex-col items-center">
                <Avatar
                  className="h-24 w-24 border-4 shadow-md"
                  style={{ borderColor: COLORS.white }}
                >
                  {currentProfile?.image ? (
                    <AvatarImage
                      src={currentProfile.image}
                      alt="Photo de profil"
                    />
                  ) : (
                    <AvatarFallback
                      style={{
                        backgroundColor: COLORS.green,
                        color: COLORS.white,
                      }}
                    >
                      <User size={36} />
                    </AvatarFallback>
                  )}
                </Avatar>
                <h2 className="mt-4 text-xl font-bold">
                  {currentUserDetails?.name || currentUser?.name || "Client"}
                </h2>
                <p className="text-sm" style={{ color: COLORS.gray }}>
                  {/* {console.log(currentUserDetails)} */}
                  Client depuis{" "}
                  {currentUserDetails?.createdAt
                    ? formatDate(currentUserDetails.createdAt)
                    : "N/A"}
                </p>
                <Badge
                  className="mt-2"
                  style={{
                    backgroundColor: COLORS.lightOrange,
                    color: COLORS.orange,
                  }}
                >
                  {totalCommandes > 5 ? "Client fidèle" : "Nouveau client"}
                </Badge>
              </div>

              <div className="mt-6 grid grid-cols-2 gap-4">
                <div
                  className="p-3 rounded-lg flex flex-col items-center justify-center"
                  style={{ backgroundColor: COLORS.paleGreen }}
                >
                  <div
                    className="rounded-full p-2 mb-2"
                    style={{ backgroundColor: COLORS.lightGreen }}
                  >
                    <ShoppingBag size={18} style={{ color: COLORS.green }} />
                  </div>
                  <p
                    className="text-sm font-medium"
                    style={{ color: COLORS.darkGreen }}
                  >
                    Commandes
                  </p>
                  <p
                    className="text-lg font-bold"
                    style={{ color: COLORS.green }}
                  >
                    {totalCommandes}
                  </p>
                </div>

                <div
                  className="p-3 rounded-lg flex flex-col items-center justify-center"
                  style={{ backgroundColor: COLORS.lightOrange }}
                >
                  <div
                    className="rounded-full p-2 mb-2"
                    style={{
                      backgroundColor: COLORS.orange,
                      color: COLORS.white,
                    }}
                  >
                    <Package size={18} />
                  </div>
                  <p
                    className="text-sm font-medium"
                    style={{ color: COLORS.orange }}
                  >
                    En cours
                  </p>
                  <p
                    className="text-lg font-bold"
                    style={{ color: COLORS.orange }}
                  >
                    {enCoursCommandes}
                  </p>
                </div>
              </div>

              <div
                className="mt-6 pt-6 border-t"
                style={{ borderColor: COLORS.lightGray }}
              >
                <div className="flex items-center space-x-3 mb-3">
                  <Mail size={18} style={{ color: COLORS.gray }} />
                  <span
                    className="text-sm truncate"
                    style={{ color: COLORS.gray }}
                  >
                    {currentUserDetails?.email || "Email non disponible"}
                  </span>
                </div>
                <div className="flex items-center space-x-3 mb-3">
                  <Phone size={18} style={{ color: COLORS.gray }} />
                  <span className="text-sm" style={{ color: COLORS.gray }}>
                    {currentProfile?.numero ||
                      currentUser?.numero ||
                      "Téléphone non disponible"}
                  </span>
                </div>
                <div className="flex items-center space-x-3">
                  <MapPin size={18} style={{ color: COLORS.gray }} />
                  <span className="text-sm" style={{ color: COLORS.gray }}>
                    {currentUser?.quartier
                      ? `${currentUser.quartier}, ${currentUser.region || ""}`
                      : "Adresse non disponible"}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card style={{ backgroundColor: COLORS.white }}>
            <CardHeader>
              <CardTitle style={{ color: COLORS.green }}>
                Contacter le client
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form className="space-y-4">
                <div className="space-y-2">
                  <Label
                    htmlFor="contact-method"
                    style={{ color: COLORS.darkGreen }}
                  >
                    Méthode de contact
                  </Label>
                  <Select>
                    <SelectTrigger
                      id="contact-method"
                      style={{
                        borderColor: COLORS.lightGreen,
                        backgroundColor: COLORS.white,
                      }}
                    >
                      <SelectValue placeholder="Choisir une méthode" />
                    </SelectTrigger>
                    <SelectContent>
                      {/* <SelectItem value="direct">Message direct</SelectItem> */}
                      <SelectItem value="email">Email</SelectItem>
                      {/* <SelectItem value="whatsapp">WhatsApp</SelectItem> */}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="message" style={{ color: COLORS.darkGreen }}>
                    Message
                  </Label>
                  <Textarea
                    id="message"
                    placeholder="Écrivez votre message ici..."
                    style={{
                      borderColor: COLORS.lightGreen,
                      backgroundColor: COLORS.white,
                    }}
                    value={message}
                    onChange={handleChange}
                  />
                </div>
                <Button
                  onClick={handleSendEmail}
                  type="submit"
                  className="w-full flex items-center justify-center"
                  disabled={isLoading} // désactive le bouton pendant l'envoi
                  style={{
                    backgroundColor: COLORS.green,
                    opacity: isLoading ? 0.7 : 1, // effet visuel léger pendant chargement
                    cursor: isLoading ? "not-allowed" : "pointer",
                  }}
                >
                  {isLoading ? (
                    <>
                      <Loader className="mr-2 h-4 w-4 animate-spin" />
                      Envoi en cours...
                    </>
                  ) : (
                    <>
                      <Send className="mr-2 h-4 w-4" />
                      Envoyer
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>

        {/* Colonnes de droite - Informations et commandes */}
        <div className="lg:col-span-2 space-y-6">
          <Tabs
            defaultValue="profile"
            className="w-full"
            onValueChange={setActiveTab}
          >
            <TabsList
              className="grid grid-cols-3 mb-6 w-full"
              style={{ backgroundColor: COLORS.lightGreen }}
            >
              <TabsTrigger
                value="profile"
                style={{
                  color: activeTab === "profile" ? COLORS.white : COLORS.green,
                  backgroundColor:
                    activeTab === "profile" ? COLORS.green : "transparent",
                }}
              >
                Profil
              </TabsTrigger>
              <TabsTrigger
                value="commandes"
                style={{
                  color:
                    activeTab === "commandes" ? COLORS.white : COLORS.green,
                  backgroundColor:
                    activeTab === "commandes" ? COLORS.green : "transparent",
                }}
              >
                Commandes
              </TabsTrigger>
              <TabsTrigger
                value="stats"
                style={{
                  color: activeTab === "stats" ? COLORS.white : COLORS.green,
                  backgroundColor:
                    activeTab === "stats" ? COLORS.green : "transparent",
                }}
              >
                Statistiques
              </TabsTrigger>
            </TabsList>

            <TabsContent value="profile">
              <Card style={{ backgroundColor: COLORS.white }}>
                <CardHeader>
                  <CardTitle style={{ color: COLORS.green }}>
                    Informations personnelles
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h3
                        className="text-sm font-medium mb-2"
                        style={{ color: COLORS.darkGreen }}
                      >
                        Détails du compte
                      </h3>
                      <div className="space-y-4">
                        <div className="bg-gray-50 p-3 rounded-md">
                          <p className="text-xs" style={{ color: COLORS.gray }}>
                            Nom complet
                          </p>
                          <p className="font-medium">
                            {currentUserDetails?.name ||
                              currentUser?.name ||
                              "Non spécifié"}
                          </p>
                        </div>
                        <div className="bg-gray-50 p-3 rounded-md">
                          <p className="text-xs" style={{ color: COLORS.gray }}>
                            Email
                          </p>
                          <p className="font-medium">
                            {currentUserDetails?.email || "Non spécifié"}
                          </p>
                        </div>
                        <div className="bg-gray-50 p-3 rounded-md">
                          <p className="text-xs" style={{ color: COLORS.gray }}>
                            Date d'inscription
                          </p>
                          <p className="font-medium">
                            {currentUserDetails?.createdAt
                              ? formatDate(currentUserDetails.createdAt)
                              : "Non spécifié"}
                          </p>
                        </div>
                        {/* <div className="bg-gray-50 p-3 rounded-md">
                          <p className="text-xs" style={{ color: COLORS.gray }}>
                            WhatsApp
                          </p>
                          <Badge
                            style={{
                              backgroundColor: currentUserDetails?.whatsapp
                                ? COLORS.lightGreen
                                : COLORS.lightGray,
                              color: currentUserDetails?.whatsapp
                                ? COLORS.green
                                : COLORS.gray,
                            }}
                          >
                            {currentUserDetails?.whatsapp
                              ? "Disponible"
                              : "Non disponible"}
                          </Badge>
                        </div> */}
                      </div>
                    </div>

                    <div>
                      <h3
                        className="text-sm font-medium mb-2"
                        style={{ color: COLORS.darkGreen }}
                      >
                        Coordonnées
                      </h3>
                      <div className="space-y-4">
                        <div className="bg-gray-50 p-3 rounded-md">
                          <p className="text-xs" style={{ color: COLORS.gray }}>
                            Téléphone
                          </p>
                          <p className="font-medium">
                            {currentProfile?.numero ||
                              currentUser?.numero ||
                              "Non spécifié"}
                          </p>
                        </div>
                        <div className="bg-gray-50 p-3 rounded-md">
                          <p className="text-xs" style={{ color: COLORS.gray }}>
                            Pays
                          </p>
                          <p className="font-medium">
                            {currentUser?.region || "Non spécifié"}
                          </p>
                        </div>
                        <div className="bg-gray-50 p-3 rounded-md">
                          <p className="text-xs" style={{ color: COLORS.gray }}>
                            Ville | Village | Commune
                          </p>
                          <p className="font-medium">
                            {currentUser?.quartier || "Non spécifié"}
                          </p>
                        </div>
                        {/* <div className="bg-gray-50 p-3 rounded-md">
                          <p className="text-xs" style={{ color: COLORS.gray }}>
                            Notes
                          </p>
                          <p className="font-medium">Notes sur le client</p>
                        </div> */}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="commandes">
              <Card style={{ backgroundColor: COLORS.white }}>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle style={{ color: COLORS.green }}>
                    Historique des commandes
                  </CardTitle>
                  <Badge style={{ backgroundColor: COLORS.orange }}>
                    {totalCommandes} commande{totalCommandes > 1 ? "s" : ""}
                  </Badge>
                </CardHeader>
                <CardContent>
                  {userCommandes.length > 0 ? (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader
                          style={{ backgroundColor: COLORS.lightGreen }}
                        >
                          <TableRow>
                            <TableHead style={{ color: COLORS.darkGreen }}>
                              Réf. Commande
                            </TableHead>
                            <TableHead style={{ color: COLORS.darkGreen }}>
                              Date
                            </TableHead>
                            <TableHead style={{ color: COLORS.darkGreen }}>
                              Montant
                            </TableHead>
                            <TableHead style={{ color: COLORS.darkGreen }}>
                              Statut
                            </TableHead>
                            <TableHead
                              style={{ color: COLORS.darkGreen }}
                            ></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {userCommandes.map((commande) => (
                            <TableRow
                              key={commande._id}
                              className="hover:bg-gray-50"
                            >
                              <TableCell className="font-medium">
                                {commande._id.substring(0, 8)}...
                              </TableCell>
                              <TableCell>
                                {formatDate(
                                  commande.date || commande.createdAt
                                )}
                              </TableCell>
                              <TableCell>
                                {formatCurrency(commande.prixTotal || 0)}
                              </TableCell>
                              <TableCell>
                                <Badge
                                  style={{
                                    backgroundColor:
                                      commande.statusLivraison === "livré"
                                        ? COLORS.lightGreen
                                        : commande.statusLivraison ===
                                          "en cours"
                                        ? COLORS.lightOrange
                                        : COLORS.lightGray,
                                    color:
                                      commande.statusLivraison === "livré"
                                        ? COLORS.green
                                        : commande.statusLivraison ===
                                          "en cours"
                                        ? COLORS.orange
                                        : COLORS.gray,
                                  }}
                                >
                                  {commande.statusLivraison || "En attente"}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  style={{ color: COLORS.green }}
                                >
                                  <ChevronRight size={16} />
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  ) : (
                    <div className="text-center py-6">
                      <Package size={48} className="mx-auto opacity-20 mb-4" />
                      <p className="text-gray-500">
                        Aucune commande trouvée pour ce client
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="stats">
              <Card style={{ backgroundColor: COLORS.white }}>
                <CardHeader>
                  <CardTitle style={{ color: COLORS.green }}>
                    Statistiques client
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <div
                      className="p-4 rounded-lg text-center"
                      style={{ backgroundColor: COLORS.lightGreen }}
                    >
                      <h3
                        className="text-sm font-medium mb-1"
                        style={{ color: COLORS.darkGreen }}
                      >
                        Total des commandes
                      </h3>
                      <p
                        className="text-2xl font-bold"
                        style={{ color: COLORS.green }}
                      >
                        {totalCommandes}
                      </p>
                    </div>
                    <div
                      className="p-4 rounded-lg text-center"
                      style={{ backgroundColor: COLORS.lightOrange }}
                    >
                      <h3
                        className="text-sm font-medium mb-1"
                        style={{ color: COLORS.orange }}
                      >
                        En cours
                      </h3>
                      <p
                        className="text-2xl font-bold"
                        style={{ color: COLORS.orange }}
                      >
                        {enCoursCommandes}
                      </p>
                    </div>
                    <div
                      className="p-4 rounded-lg text-center"
                      style={{ backgroundColor: COLORS.paleGreen }}
                    >
                      <h3
                        className="text-sm font-medium mb-1"
                        style={{ color: COLORS.darkGreen }}
                      >
                        Livrées
                      </h3>
                      <p
                        className="text-2xl font-bold"
                        style={{ color: COLORS.green }}
                      >
                        {livreesCommandes}
                      </p>
                    </div>
                    <div
                      className="p-4 rounded-lg text-center"
                      style={{ backgroundColor: COLORS.lightGray }}
                    >
                      <h3
                        className="text-sm font-medium mb-1"
                        style={{ color: COLORS.gray }}
                      >
                        Total dépensé
                      </h3>
                      <p
                        className="text-2xl font-bold"
                        style={{ color: COLORS.green }}
                      >
                        {formatCurrency(totalDepenses)}
                      </p>
                    </div>
                  </div>

                  <div className="space-y-6">
                    <div>
                      <h3
                        className="text-sm font-medium mb-3"
                        style={{ color: COLORS.darkGreen }}
                      >
                        Activité récente
                      </h3>
                      {userCommandes.length > 0 ? (
                        <div className="space-y-3">
                          {userCommandes.slice(0, 5).map((commande) => (
                            <div
                              key={commande._id}
                              className="flex items-center justify-between p-3 rounded-md bg-gray-50"
                            >
                              <div className="flex items-center space-x-3">
                                <div
                                  className="rounded-full p-2"
                                  style={{ backgroundColor: COLORS.lightGreen }}
                                >
                                  <ShoppingBag
                                    size={16}
                                    style={{ color: COLORS.green }}
                                  />
                                </div>
                                <div>
                                  <p className="text-sm font-medium">
                                    Commande #{commande._id.substring(0, 6)}
                                  </p>
                                  <p
                                    className="text-xs"
                                    style={{ color: COLORS.gray }}
                                  >
                                    {formatDate(commande.date)}
                                  </p>
                                </div>
                              </div>
                              <p className="font-medium">
                                {formatCurrency(commande.prixTotal || 0)}
                              </p>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500">
                          Aucune activité récente
                        </p>
                      )}
                    </div>

                    <div>
                      <h3
                        className="text-sm font-medium mb-3"
                        style={{ color: COLORS.darkGreen }}
                      >
                        Actions administratives
                      </h3>
                      <div className="flex flex-wrap gap-3">
                        <Button
                          variant="outline"
                          className="flex items-center"
                          style={{
                            borderColor: COLORS.green,
                            color: COLORS.green,
                          }}
                        >
                          <MessageCircle size={16} className="mr-2" />
                          Envoyer un message
                        </Button>
                        <Button
                          variant="destructive"
                          className="flex items-center"
                          style={{
                            backgroundColor: "#FEE2E2",
                            color: "#EF4444",
                            border: "1px solid #FCA5A5",
                          }}
                        >
                          <Trash2 size={16} className="mr-2" />
                          Supprimer le compte
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}

export default ModernCustomerDetails;
